import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Groups as GroupsIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Role {
  roleId: string;
  roleName: string;
  isPlatformRole?: boolean;
}

interface Location {
  locationId: string;
  locationName: string;
}

interface Member {
  member_id: string;
  first_name: string;
  last_name: string;
  display_order: number;
  is_active: boolean;
}

interface SharedAccount {
  shared_account_id: string;
  account_name: string;
  email: string;
  role_name: string;
  role_id: string;
  location_name: string | null;
  location_id: string | null;
  is_active: boolean;
  member_count: number;
  created_at: string;
  members?: Member[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const EMPTY_ACCOUNT_FORM = {
  account_name: '',
  email: '',
  password: '',
  role_id: '',
  location_id: '',
};

const EMPTY_MEMBER_FORM = {
  first_name: '',
  last_name: '',
  pin: '',
  display_order: '0',
};

// ─── Component ────────────────────────────────────────────────────────────────

const SharedAccountManagement: React.FC = () => {
  const [accounts, setAccounts] = useState<SharedAccount[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Account dialog state
  const [accountDialog, setAccountDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<SharedAccount | null>(null);
  const [accountForm, setAccountForm] = useState(EMPTY_ACCOUNT_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountError, setAccountError] = useState('');

  // Password change dialog
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordAccountId, setPasswordAccountId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Members panel
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);
  const [expandedMembers, setExpandedMembers] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Member dialog state
  const [memberDialog, setMemberDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [memberAccountId, setMemberAccountId] = useState('');
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER_FORM);
  const [showPin, setShowPin] = useState(false);
  const [memberSaving, setMemberSaving] = useState(false);
  const [memberError, setMemberError] = useState('');

  // Delete confirms
  const [deleteAccountId, setDeleteAccountId] = useState<string | null>(null);
  const [deleteMemberId, setDeleteMemberId] = useState<string | null>(null);
  const [deleteMemberAccountId, setDeleteMemberAccountId] = useState('');

  // ─── Data loading ────────────────────────────────────────────────────────

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/shared-accounts`, { headers: authHeader() });
      setAccounts(res.data);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load shared accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRolesAndLocations = useCallback(async () => {
    try {
      const [rolesRes, locationsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/roles`, { headers: authHeader() }),
        axios.get(`${API_BASE_URL}/locations`, { headers: authHeader() }),
      ]);
      setRoles(
        (rolesRes.data as Role[]).filter((r) => !r.isPlatformRole)
      );
      setLocations(locationsRes.data);
    } catch {
      // Non-fatal
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
    fetchRolesAndLocations();
  }, [fetchAccounts, fetchRolesAndLocations]);

  // ─── Expand members ──────────────────────────────────────────────────────

  const toggleExpand = async (account: SharedAccount) => {
    if (expandedAccountId === account.shared_account_id) {
      setExpandedAccountId(null);
      return;
    }
    setExpandedAccountId(account.shared_account_id);
    setMembersLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/shared-accounts/${account.shared_account_id}`,
        { headers: authHeader() }
      );
      setExpandedMembers(res.data.members || []);
    } catch {
      setExpandedMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  // ─── Account CRUD ────────────────────────────────────────────────────────

  const openCreateAccount = () => {
    setEditingAccount(null);
    setAccountForm(EMPTY_ACCOUNT_FORM);
    setShowPassword(false);
    setAccountError('');
    setAccountDialog(true);
  };

  const openEditAccount = (account: SharedAccount) => {
    setEditingAccount(account);
    setAccountForm({
      account_name: account.account_name,
      email: account.email,
      password: '',
      role_id: account.role_id,
      location_id: account.location_id || '',
    });
    setShowPassword(false);
    setAccountError('');
    setAccountDialog(true);
  };

  const saveAccount = async () => {
    const { account_name, email, password, role_id } = accountForm;
    if (!account_name.trim() || !email.trim() || !role_id) {
      setAccountError('Account name, email and role are required');
      return;
    }
    if (!editingAccount && password.length < 8) {
      setAccountError('Password must be at least 8 characters');
      return;
    }

    setAccountSaving(true);
    setAccountError('');
    try {
      if (editingAccount) {
        await axios.put(
          `${API_BASE_URL}/shared-accounts/${editingAccount.shared_account_id}`,
          {
            account_name: accountForm.account_name,
            email: accountForm.email,
            role_id: accountForm.role_id,
            location_id: accountForm.location_id || null,
          },
          { headers: authHeader() }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/shared-accounts`,
          {
            account_name: accountForm.account_name,
            email: accountForm.email,
            password: accountForm.password,
            role_id: accountForm.role_id,
            location_id: accountForm.location_id || null,
          },
          { headers: authHeader() }
        );
      }
      setAccountDialog(false);
      fetchAccounts();
    } catch (e: any) {
      setAccountError(e.response?.data?.error || 'Failed to save account');
    } finally {
      setAccountSaving(false);
    }
  };

  const deleteAccount = async (id: string) => {
    try {
      await axios.delete(`${API_BASE_URL}/shared-accounts/${id}`, { headers: authHeader() });
      setDeleteAccountId(null);
      fetchAccounts();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to delete account');
    }
  };

  // ─── Password change ─────────────────────────────────────────────────────

  const openPasswordDialog = (accountId: string) => {
    setPasswordAccountId(accountId);
    setNewPassword('');
    setShowNewPassword(false);
    setPasswordError('');
    setPasswordDialog(true);
  };

  const savePassword = async () => {
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    setPasswordSaving(true);
    setPasswordError('');
    try {
      await axios.put(
        `${API_BASE_URL}/shared-accounts/${passwordAccountId}/password`,
        { password: newPassword },
        { headers: authHeader() }
      );
      setPasswordDialog(false);
    } catch (e: any) {
      setPasswordError(e.response?.data?.error || 'Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  // ─── Member CRUD ─────────────────────────────────────────────────────────

  const openAddMember = (accountId: string) => {
    setEditingMember(null);
    setMemberAccountId(accountId);
    setMemberForm(EMPTY_MEMBER_FORM);
    setShowPin(false);
    setMemberError('');
    setMemberDialog(true);
  };

  const openEditMember = (accountId: string, member: Member) => {
    setEditingMember(member);
    setMemberAccountId(accountId);
    setMemberForm({
      first_name: member.first_name,
      last_name: member.last_name,
      pin: '',
      display_order: String(member.display_order),
    });
    setShowPin(false);
    setMemberError('');
    setMemberDialog(true);
  };

  const saveMember = async () => {
    const { first_name, last_name, pin } = memberForm;
    if (!first_name.trim() || !last_name.trim()) {
      setMemberError('First name and last name are required');
      return;
    }
    if (!editingMember && !/^\d{4,8}$/.test(pin)) {
      setMemberError('PIN must be 4–8 digits');
      return;
    }
    if (editingMember && pin && !/^\d{4,8}$/.test(pin)) {
      setMemberError('PIN must be 4–8 digits');
      return;
    }

    setMemberSaving(true);
    setMemberError('');
    try {
      const payload: any = {
        first_name: memberForm.first_name,
        last_name: memberForm.last_name,
        display_order: Number(memberForm.display_order) || 0,
      };
      if (pin) payload.pin = pin;

      if (editingMember) {
        await axios.put(
          `${API_BASE_URL}/shared-accounts/${memberAccountId}/members/${editingMember.member_id}`,
          payload,
          { headers: authHeader() }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/shared-accounts/${memberAccountId}/members`,
          { ...payload, pin: memberForm.pin },
          { headers: authHeader() }
        );
      }
      setMemberDialog(false);
      // Refresh members panel
      const res = await axios.get(
        `${API_BASE_URL}/shared-accounts/${memberAccountId}`,
        { headers: authHeader() }
      );
      setExpandedMembers(res.data.members || []);
      fetchAccounts();
    } catch (e: any) {
      setMemberError(e.response?.data?.error || 'Failed to save member');
    } finally {
      setMemberSaving(false);
    }
  };

  const deleteMember = async () => {
    if (!deleteMemberId || !deleteMemberAccountId) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/shared-accounts/${deleteMemberAccountId}/members/${deleteMemberId}`,
        { headers: authHeader() }
      );
      setDeleteMemberId(null);
      const res = await axios.get(
        `${API_BASE_URL}/shared-accounts/${deleteMemberAccountId}`,
        { headers: authHeader() }
      );
      setExpandedMembers(res.data.members || []);
      fetchAccounts();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to remove member');
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Not-recommended banner ── */}
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        sx={{ mb: 3, borderRadius: 2 }}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Shared Accounts are not recommended for most teams
        </Typography>
        <Typography variant="body2">
          A shared account lets a group of users log in under one set of credentials
          and then select their name from a till-style screen using a personal PIN.
          Because all activity is attributed to a single login, you lose individual
          audit trails, email-based communications (roundups, alerts, password resets)
          and push notification targeting. For the best experience — especially for
          compliance, reporting and communications — we strongly recommend every user
          has their own account with a personal email address.
        </Typography>
      </Alert>

      {/* ── Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <GroupsIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Shared Accounts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateAccount}
        >
          New Shared Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : accounts.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={8}
          color="text.secondary"
        >
          <GroupsIcon sx={{ fontSize: 56, mb: 2, opacity: 0.3 }} />
          <Typography>No shared accounts yet.</Typography>
          <Button sx={{ mt: 2 }} variant="outlined" startIcon={<AddIcon />} onClick={openCreateAccount}>
            Create your first shared account
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {accounts.map((account) => (
            <Grid item xs={12} key={account.shared_account_id}>
              <Card variant="outlined">
                <CardContent sx={{ pb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {account.account_name}
                        </Typography>
                        <Chip
                          size="small"
                          label={account.is_active ? 'Active' : 'Inactive'}
                          color={account.is_active ? 'success' : 'default'}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {account.email}
                      </Typography>
                      <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                        <Chip size="small" label={`Role: ${account.role_name}`} variant="outlined" />
                        {account.location_name && (
                          <Chip size="small" label={`Location: ${account.location_name}`} variant="outlined" />
                        )}
                        <Chip
                          size="small"
                          icon={<GroupsIcon />}
                          label={`${account.member_count} member${account.member_count !== 1 ? 's' : ''}`}
                          variant="outlined"
                          color="primary"
                        />
                      </Box>
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Change password">
                        <IconButton size="small" onClick={() => openPasswordDialog(account.shared_account_id)}>
                          <LockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit account">
                        <IconButton size="small" onClick={() => openEditAccount(account)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete account">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteAccountId(account.shared_account_id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                  <Button
                    size="small"
                    onClick={() => toggleExpand(account)}
                  >
                    {expandedAccountId === account.shared_account_id ? 'Hide Members' : 'Manage Members'}
                  </Button>
                </CardActions>

                {expandedAccountId === account.shared_account_id && (
                  <>
                    <Divider />
                    <Box px={2} pb={2} pt={1.5}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Members (till screen)
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={() => openAddMember(account.shared_account_id)}
                        >
                          Add Member
                        </Button>
                      </Box>

                      {membersLoading ? (
                        <Box display="flex" justifyContent="center" py={2}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : expandedMembers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          No members yet. Add members so they can select themselves from the till screen.
                        </Typography>
                      ) : (
                        <List dense disablePadding>
                          {expandedMembers.map((member) => (
                            <ListItem
                              key={member.member_id}
                              disableGutters
                              divider
                              sx={{ py: 0.5 }}
                            >
                              <ListItemText
                                primary={`${member.first_name} ${member.last_name}`}
                                secondary={`Display order: ${member.display_order}`}
                              />
                              <ListItemSecondaryAction>
                                <Tooltip title="Edit member">
                                  <IconButton
                                    size="small"
                                    onClick={() => openEditMember(account.shared_account_id, member)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove member">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setDeleteMemberId(member.member_id);
                                      setDeleteMemberAccountId(account.shared_account_id);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Create / Edit Account Dialog ── */}
      <Dialog open={accountDialog} onClose={() => setAccountDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAccount ? 'Edit Shared Account' : 'Create Shared Account'}</DialogTitle>
        <DialogContent>
          {accountError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {accountError}
            </Alert>
          )}
          <TextField
            label="Account Name"
            value={accountForm.account_name}
            onChange={(e) => setAccountForm((f) => ({ ...f, account_name: e.target.value }))}
            fullWidth
            margin="normal"
            placeholder="e.g. Workshop Team"
            helperText="This name appears as the account label in the web admin."
          />
          <TextField
            label="Login Email"
            value={accountForm.email}
            onChange={(e) => setAccountForm((f) => ({ ...f, email: e.target.value }))}
            fullWidth
            margin="normal"
            type="email"
            helperText="The email address used to log in to this shared account on the mobile app."
          />
          {!editingAccount && (
            <TextField
              label="Password"
              value={accountForm.password}
              onChange={(e) => setAccountForm((f) => ({ ...f, password: e.target.value }))}
              fullWidth
              margin="normal"
              type={showPassword ? 'text' : 'password'}
              helperText="Minimum 8 characters. Share this with the team who will be using this account."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <TextField
            select
            label="Role"
            value={accountForm.role_id}
            onChange={(e) => setAccountForm((f) => ({ ...f, role_id: e.target.value }))}
            fullWidth
            margin="normal"
            helperText="All members of this shared account will act under this role."
          >
            {roles.map((r) => (
              <MenuItem key={r.roleId} value={r.roleId}>
                {r.roleName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Location (optional)"
            value={accountForm.location_id}
            onChange={(e) => setAccountForm((f) => ({ ...f, location_id: e.target.value }))}
            fullWidth
            margin="normal"
          >
            <MenuItem value="">— No specific location —</MenuItem>
            {locations.map((l) => (
              <MenuItem key={l.locationId} value={l.locationId}>
                {l.locationName}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialog(false)} disabled={accountSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveAccount} disabled={accountSaving}>
            {accountSaving ? <CircularProgress size={20} /> : editingAccount ? 'Save Changes' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Change Password Dialog ── */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Shared Account Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            margin="normal"
            type={showNewPassword ? 'text' : 'password'}
            helperText="Minimum 8 characters."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword((v) => !v)} edge="end">
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)} disabled={passwordSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={savePassword} disabled={passwordSaving}>
            {passwordSaving ? <CircularProgress size={20} /> : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add / Edit Member Dialog ── */}
      <Dialog open={memberDialog} onClose={() => setMemberDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingMember ? 'Edit Member' : 'Add Member'}</DialogTitle>
        <DialogContent>
          {memberError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {memberError}
            </Alert>
          )}
          <TextField
            label="First Name"
            value={memberForm.first_name}
            onChange={(e) => setMemberForm((f) => ({ ...f, first_name: e.target.value }))}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Last Name"
            value={memberForm.last_name}
            onChange={(e) => setMemberForm((f) => ({ ...f, last_name: e.target.value }))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={editingMember ? 'New PIN (leave blank to keep current)' : 'PIN'}
            value={memberForm.pin}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '');
              setMemberForm((f) => ({ ...f, pin: v }));
            }}
            fullWidth
            margin="normal"
            type={showPin ? 'text' : 'password'}
            inputProps={{ maxLength: 8 }}
            helperText="4–8 digits. This is the PIN the member enters on the till screen."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPin((v) => !v)} edge="end">
                    {showPin ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Display Order"
            value={memberForm.display_order}
            onChange={(e) => setMemberForm((f) => ({ ...f, display_order: e.target.value }))}
            fullWidth
            margin="normal"
            type="number"
            helperText="Controls the order members appear on the till screen (lower = first)."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberDialog(false)} disabled={memberSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveMember} disabled={memberSaving}>
            {memberSaving ? <CircularProgress size={20} /> : editingMember ? 'Save Changes' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Account Confirm ── */}
      <Dialog open={!!deleteAccountId} onClose={() => setDeleteAccountId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Shared Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this shared account? All members will also be removed.
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAccountId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteAccountId && deleteAccount(deleteAccountId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Member Confirm ── */}
      <Dialog open={!!deleteMemberId} onClose={() => setDeleteMemberId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          <Typography>
            Remove this member from the shared account? They will no longer appear on the till screen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMemberId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteMember}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SharedAccountManagement;
